import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { isNewStyle } from '@/ui/artStyle';
import { tone } from './palette';

export const FONT = '"Noto Sans TC","PingFang TC","Microsoft JhengHei",system-ui,sans-serif';
/** v1.4 受光材質：經典版＝Lambert（原本的樣子）；新版＝Standard（有粗糙度、反光，配合環境光與陰影） */
export type LitMaterial = THREE.MeshLambertMaterial;
type LitParams = { color?: THREE.ColorRepresentation; emissive?: THREE.ColorRepresentation; transparent?: boolean; opacity?: number; depthWrite?: boolean; map?: THREE.Texture | null; side?: THREE.Side };
export function litMat(p: LitParams): LitMaterial {
  if (!isNewStyle()) return new THREE.MeshLambertMaterial(p);
  const c = p.color != null ? tone(p.color) : undefined;
  const m = new THREE.MeshStandardMaterial({ ...p, color: c, roughness: 0.62, metalness: 0.04 });
  return m as unknown as LitMaterial;
}
const matCache = new Map<string, LitMaterial>();
/** 同色材質共用，減少 GPU 物件數量 */
export function mat(color: string | number, emissive?: string | number, opacity = 1): LitMaterial {
  const key = `${color}|${emissive ?? ''}|${opacity}`;
  let m = matCache.get(key);
  if (!m) {
    m = litMat({ color, emissive: emissive ?? 0x000000, transparent: opacity < 1, opacity });
    matCache.set(key, m);
  }
  return m;
}
export const uniqueMat = (color: string | number, emissive?: string | number) => litMat({ color, emissive: emissive ?? 0x000000 });

/** v1.4 方塊：新版用圓角方塊（太薄的維持直角，避免變形）；經典版＝一般方塊 */
export function box(w: number, h: number, d: number): THREE.BufferGeometry {
  if (!isNewStyle()) return new THREE.BoxGeometry(w, h, d);
  const m = Math.min(w, h, d);
  if (m < 0.09) return new THREE.BoxGeometry(w, h, d);
  // v1.4.1 同尺寸共用一份（籃子、烏龜車一直重建，不用每次重算圓角）
  const key = `${w.toFixed(2)}|${h.toFixed(2)}|${d.toFixed(2)}`;
  let g = boxCache.get(key);
  if (!g) { g = new RoundedBoxGeometry(w, h, d, 2, Math.min(0.08, m * 0.18)); g.userData.shared = true; boxCache.set(key, g); }
  return g;
}
const boxCache = new Map<string, THREE.BufferGeometry>();

function rr(x: CanvasRenderingContext2D, a: number, b: number, w: number, h: number, r: number) {
  x.beginPath(); x.moveTo(a + r, b); x.arcTo(a + w, b, a + w, b + h, r); x.arcTo(a + w, b + h, a, b + h, r);
  x.arcTo(a, b + h, a, b, r); x.arcTo(a, b, a + w, b, r); x.closePath();
}

/** v1.2 文字太長時換行（英文以空白斷字、中文逐字），回傳最多 maxLines 行 */
function wrapText(x: CanvasRenderingContext2D, text: string, maxW: number, maxLines = 2): string[] {
  if (x.measureText(text).width <= maxW) return [text];
  const words = /\s/.test(text) ? text.split(/(\s+)/).filter(Boolean) : [...text];
  const lines: string[] = []; let cur = '';
  for (const w of words) {
    const next = cur + w;
    if (cur && x.measureText(next.trimEnd()).width > maxW) { lines.push(cur.trim()); cur = w.trimStart(); }
    else cur = next;
  }
  if (cur.trim()) lines.push(cur.trim());
  if (lines.length > maxLines) { const rest = lines.slice(maxLines - 1).join(' '); lines.length = maxLines - 1; lines.push(rest); }
  return lines;
}

/** v1.2 名牌整體放大倍率（手機、英文版字比較多時放大） */
let LABEL_SCALE = 1;
export function setLabelScale(k: number): void { LABEL_SCALE = k; }

/** 頭上的名牌。v1.2：不再把長字（英文）硬擠進固定寬度——字太長就加寬、再長就換兩行，字的大小不變 */
export function makeLabel(title: string, sub?: string, opts: { bg?: string; scale?: number } = {}): THREE.Sprite {
  const TF = `700 46px ${FONT}`, SF = `500 30px ${FONT}`, MAXW = 860;
  const m = document.createElement('canvas').getContext('2d')!;
  m.font = TF; const tl = wrapText(m, title, MAXW); const w1 = Math.max(...tl.map(l => m.measureText(l).width));
  m.font = SF; const sl = sub ? wrapText(m, sub, MAXW, 1) : []; const w2 = sl.length ? m.measureText(sl[0]).width : 0;
  const cw = Math.max(640, Math.ceil(Math.max(w1, w2) + 56));
  const h = 30 + tl.length * 52 + (sl.length ? 44 : 0), ch = Math.max(150, h + 8);
  const cv = document.createElement('canvas'); cv.width = cw; cv.height = ch;
  const x = cv.getContext('2d')!;
  const w = Math.min(cw - 8, Math.max(w1, w2) + 48), top = (ch - h) / 2;
  // v1.4 新版：奶油色名牌、深棕字（有指定底色的維持原色）
  const cream = isNewStyle() && !opts.bg;
  x.fillStyle = opts.bg ?? (cream ? 'rgba(251,246,236,0.94)' : 'rgba(16,26,32,0.82)'); rr(x, (cw - w) / 2, top, w, h, cream ? 26 : 20); x.fill();
  if (cream) { x.lineWidth = 3; x.strokeStyle = 'rgba(80,60,40,0.28)'; x.stroke(); }
  x.textAlign = 'center'; x.textBaseline = 'middle';
  x.fillStyle = cream ? '#3b2f2a' : '#f3f6f5'; x.font = TF; tl.forEach((l, i) => x.fillText(l, cw / 2, top + 41 + i * 52));
  if (sl.length) { x.fillStyle = cream ? '#6e5b4d' : '#b9c8cf'; x.font = SF; x.fillText(sl[0], cw / 2, top + 41 + tl.length * 52 - 4); }
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
  const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
  overlay(s);
  const k = (opts.scale ?? 1) * LABEL_SCALE;
  s.scale.set(2.9 * k * cw / 640, 0.68 * k * ch / 150, 1); s.renderOrder = 9; return s;
}

/** 地板上的平面文字（區域名稱）。v1.2：長字換兩行，不再壓扁 */
export function makeFloorText(text: string, color: string, width = 4): THREE.Mesh {
  const F = `800 54px ${FONT}`;
  const m = document.createElement('canvas').getContext('2d')!; m.font = F;
  const lines = wrapText(m, text, 500);
  const cw = Math.max(512, Math.ceil(Math.max(...lines.map(l => m.measureText(l).width)) + 12)), ch = 96 + (lines.length - 1) * 64;
  const cv = document.createElement('canvas'); cv.width = cw; cv.height = ch;
  const x = cv.getContext('2d')!;
  x.font = F; x.textAlign = 'center'; x.textBaseline = 'middle';
  x.fillStyle = color; lines.forEach((l, i) => x.fillText(l, cw / 2, 50 + i * 64));
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
  const pw = width * Math.min(cw, 640) / 512;
  const m2 = new THREE.Mesh(new THREE.PlaneGeometry(pw, pw * ch / cw),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0.55, depthWrite: false }));
  m2.rotation.x = -Math.PI / 2; return m2;
}

export function makeMarker(char = '!', bg = '#f2b134'): THREE.Sprite {
  const cv = document.createElement('canvas'); cv.width = cv.height = 128;
  const x = cv.getContext('2d')!;
  x.fillStyle = bg; x.beginPath(); x.arc(64, 64, 54, 0, Math.PI * 2); x.fill();
  x.fillStyle = '#1a2630'; x.font = `900 80px ${FONT}`; x.textAlign = 'center'; x.textBaseline = 'middle'; x.fillText(char, 64, 70);
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
  const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
  glowSprite(s);
  s.scale.set(0.62, 0.62, 1); s.renderOrder = 10; return s;
}

/** v1.4 新版：名牌畫在後製之後（第 1 層），不被色調、發光影響，字一樣清楚 */
export const OVERLAY_LAYER = 1;
export function overlay(o: THREE.Object3D): void {
  if (!isNewStyle()) return;
  o.layers.set(OVERLAY_LAYER);
  const m = (o as THREE.Sprite).material as THREE.Material | undefined; if (m) m.toneMapped = false;
}
/** v1.4 新版：「!」「🎮」這類標示調亮一點，後製時會發光 */
export function glowSprite(s: THREE.Sprite, k = 2.4): void {
  if (!isNewStyle()) return;
  (s.material as THREE.SpriteMaterial).color.setScalar(k);
}

export interface PersonOpts { color: string; hat?: string; mask?: boolean; apron?: string }
/** 程式生成的人物（工作服＋髮帽＋口罩）。未來有 GLB／站立圖時由 AssetManager 換掉。 */
export function makePerson(o: PersonOpts): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.38, 0.95, 18), mat(o.color)); body.position.y = 0.62;
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.25, 18, 14), mat(0xf1d3b6)); head.position.y = 1.33;
  g.add(body, head);
  if (o.apron) { const ap = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.6, 0.06), mat(o.apron)); ap.position.set(0, 0.62, 0.33); g.add(ap); }
  if (o.mask) { const mk = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.13, 0.08), mat(0xdfe8ea)); mk.position.set(0, 1.27, 0.22); g.add(mk); }
  else { const nose = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.06, 0.08), mat(0xe0b896)); nose.position.set(0, 1.32, 0.25); g.add(nose); }
  const eyeM = mat(0x1f2a30);
  for (const sx of [-0.08, 0.08]) { const e = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.04, 0.02), eyeM); e.position.set(sx, 1.38, 0.235); g.add(e); }
  if (o.hat) { const h = new THREE.Mesh(new THREE.SphereGeometry(0.28, 18, 10, 0, Math.PI * 2, 0, Math.PI / 2), mat(o.hat)); h.position.y = 1.37; g.add(h); }
  const sh = new THREE.Mesh(new THREE.CircleGeometry(0.46, 24), new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.25, depthWrite: false }));
  sh.rotation.x = -Math.PI / 2; sh.position.y = 0.015; g.add(sh);
  g.userData.body = body; return g;
}

export function disposeTree(obj: THREE.Object3D): void {
  obj.traverse(o => {
    const m = o as THREE.Mesh;
    if (m.geometry && !m.geometry.userData.shared) m.geometry.dispose();
    const mt = (m as any).material as THREE.Material | THREE.Material[] | undefined;
    const list = Array.isArray(mt) ? mt : mt ? [mt] : [];
    for (const x of list) {
      if ([...matCache.values()].includes(x as LitMaterial)) continue;
      const map = (x as any).map as THREE.Texture | undefined; if (map && !map.userData.shared) map.dispose();
      const em = (x as any).emissiveMap as THREE.Texture | undefined; if (em && !em.userData.shared) em.dispose();
      x.dispose();
    }
  });
}
